import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { HeroScreen } from './components/screens/HeroScreen';
import { QuizLayout } from './components/ui/QuizLayout';
import { QuizQuestion } from './components/screens/QuizQuestion';
import { SliderQuestion } from './components/screens/SliderQuestion';
import { ValidationScreen } from './components/screens/ValidationScreen';
import { LoadingScreen } from './components/screens/LoadingScreen';
import { ResultScreen } from './components/screens/ResultScreen';
import { SalesPage } from './components/screens/SalesPage';
import { User, UserCircle, HelpCircle, Frown, Clock, Ear, Brain, MonitorOff, Sunrise, VolumeX, Hand, BatteryWarning, Star, ShieldAlert, BookOpen, MessageCircle, HeartCrack } from 'lucide-react';
// Quiz Data Definition
const QUIZ_SCREENS = [{
  id: 'hero',
  type: 'hero'
}, {
  id: 'gender',
  type: 'single',
  title: 'Primero, cuéntanos un poco sobre tu hijo',
  subtitle: 'Mi hijo es:',
  note: 'Esto nos ayuda a adaptar el lenguaje del resultado.',
  options: [{
    id: 'boy',
    label: 'Un niño',
    icon: <User data-id="element-0" />
  }, {
    id: 'girl',
    label: 'Una niña',
    icon: <UserCircle data-id="element-1" />
  }, {
    id: 'prefer_not',
    label: 'Prefiero no decirlo',
    icon: <HelpCircle data-id="element-2" />
  }]
}, {
  id: 'age',
  type: 'single',
  title: '¿Qué edad tiene tu hijo?',
  note: 'El plan está pensado principalmente para niños de 4 a 10 años, pero muchas herramientas pueden adaptarse a otras edades.',
  options: [{
    id: '4_5',
    label: '4 a 5 años'
  }, {
    id: '6_7',
    label: '6 a 7 años'
  }, {
    id: '8_10',
    label: '8 a 10 años'
  }, {
    id: '10_plus',
    label: 'Más de 10 años'
  }]
}, {
  id: 'struggle',
  type: 'single',
  title: '¿Qué es lo que más te cuesta en casa ahora mismo?',
  noteTitle: 'Por qué preguntamos:',
  note: 'Cada familia vive el desafío de forma distinta. Identificar primero el patrón dominante ayuda a elegir herramientas más simples y aplicables.',
  options: [{
    id: 'tantrums',
    label: 'Rabietas, tensión emocional o gritos',
    icon: <Frown data-id="element-3" />
  }, {
    id: 'mornings',
    label: 'Mañanas caóticas y rutinas difíciles',
    icon: <Sunrise data-id="element-4" />
  }, {
    id: 'listening',
    label: 'Conseguir que escuche sin repetir mil veces',
    icon: <Ear data-id="element-5" />
  }, {
    id: 'focus',
    label: 'Concentración en tareas o deberes',
    icon: <Brain data-id="element-6" />
  }, {
    id: 'screens',
    label: 'Peleas por pantallas',
    icon: <MonitorOff data-id="element-7" />
  }]
}, {
  id: 'chaos',
  type: 'slider',
  title: 'De la 1 a la 10, ¿qué tan caótica se siente tu casa en los momentos difíciles?',
  note: 'A veces el problema no es falta de amor ni falta de disciplina. Es falta de un sistema simple para responder igual cada día.'
}, {
  id: 'triggers',
  type: 'multi',
  maxSelections: 2,
  title: 'Cuando tu hijo pierde el control, ¿qué suele causarlo?',
  options: [{
    id: 'transitions',
    label: 'Transiciones: dejar de jugar, salir, dormir'
  }, {
    id: 'overstimulation',
    label: 'Sobreestimulación: ruido, hermanos, lugares llenos'
  }, {
    id: 'no',
    label: 'Que le digan "no" o lo corrijan'
  }, {
    id: 'waiting',
    label: 'Esperar o aburrirse'
  }, {
    id: 'screens_off',
    label: 'Apagar pantallas'
  }, {
    id: 'expression',
    label: 'No saber cómo expresar lo que siente'
  }]
}, {
  id: 'failed',
  type: 'multi',
  title: '¿Cuáles de estos has probado, pero dejaron de funcionar después de un tiempo?',
  options: [{
    id: 'stickers',
    label: 'Tablas de pegatinas o recompensas'
  }, {
    id: 'punish_screens',
    label: 'Quitar pantallas o privilegios'
  }, {
    id: 'explain',
    label: 'Explicarle muchas veces lo mismo'
  }, {
    id: 'yell',
    label: 'Gritar, aunque no quieras hacerlo'
  }, {
    id: 'punish',
    label: 'Castigos'
  }, {
    id: 'gentle',
    label: 'Crianza suave, pero sin resultados claros'
  }]
}, {
  id: 'validation',
  type: 'validation'
}, {
  id: 'focus_q',
  type: 'single',
  title: '¿Tu hijo puede concentrarse mucho tiempo en cosas que le gustan, pero se bloquea con tareas aburridas como deberes, recoger o vestirse?',
  note: 'Esto no significa que tu hijo "no quiera". Muchos niños intensos necesitan tareas más cortas, instrucciones visibles y menos pasos al mismo tiempo.',
  options: [{
    id: 'yes',
    label: 'Sí, pasa mucho'
  }, {
    id: 'sometimes',
    label: 'A veces'
  }, {
    id: 'no',
    label: 'No tanto'
  }]
}, {
  id: 'communication',
  type: 'single',
  title: 'Cuando intentas hablar con tu hijo sobre lo que pasó, ¿la conversación suele terminar en discusión?',
  options: [{
    id: 'yes',
    label: 'Sí'
  }, {
    id: 'sometimes',
    label: 'A veces'
  }, {
    id: 'no',
    label: 'No'
  }]
}, {
  id: 'guilt',
  type: 'single',
  title: '¿Te vas a la cama sintiéndote culpable después de un día difícil con tu hijo?',
  note: 'Muchos padres aman profundamente a sus hijos, pero aun así se sienten agotados, frustrados o sin herramientas.',
  options: [{
    id: 'always',
    label: 'Casi siempre'
  }, {
    id: 'sometimes',
    label: 'Algunas veces'
  }, {
    id: 'rarely',
    label: 'Rara vez'
  }]
}, {
  id: 'future',
  type: 'single',
  title: '¿Qué preocupación te parece más cercana cuando piensas en el futuro de tu hijo?',
  options: [{
    id: 'learning',
    label: 'Que le cueste aprender o concentrarse'
  }, {
    id: 'confidence',
    label: 'Que pierda confianza en sí mismo'
  }, {
    id: 'family',
    label: 'Que las rabietas afecten la relación familiar'
  }, {
    id: 'screens',
    label: 'Que dependa demasiado de pantallas'
  }, {
    id: 'all',
    label: 'Me preocupa todo eso'
  }]
}, {
  id: 'tools',
  type: 'multi',
  maxSelections: 3,
  title: '¿Qué herramientas te ayudarían más ahora mismo?',
  options: [{
    id: 'phrases',
    label: 'Frases exactas para momentos difíciles'
  }, {
    id: 'routines',
    label: 'Rutinas visuales para mañana y noche'
  }, {
    id: 'tantrums',
    label: 'Pasos para manejar rabietas sin gritar'
  }, {
    id: 'screens',
    label: 'Estrategias para apagar pantallas sin guerra'
  }, {
    id: 'focus',
    label: 'Guía para concentración y tareas'
  }, {
    id: 'audios',
    label: 'Audios cortos para padres agotados'
  }]
}, {
  id: 'loading',
  type: 'loading'
}, {
  id: 'result',
  type: 'result'
}, {
  id: 'sales',
  type: 'sales'
}];
export function App() {
  const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  // Scroll to top on screen change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentScreenIndex]);
  const handleNext = () => {
    if (currentScreenIndex < QUIZ_SCREENS.length - 1) {
      setCurrentScreenIndex(prev => prev + 1);
    }
  };
  const handleBack = () => {
    if (currentScreenIndex > 0) {
      setCurrentScreenIndex(prev => prev - 1);
    }
  };
  const setAnswer = (questionId: string, value: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };
  const toggleMultiAnswer = (questionId: string, value: string) => {
    setAnswers(prev => {
      const current = prev[questionId] || [];
      if (current.includes(value)) {
        return {
          ...prev,
          [questionId]: current.filter((v: string) => v !== value)
        };
      } else {
        return {
          ...prev,
          [questionId]: [...current, value]
        };
      }
    });
  };
  const screen = QUIZ_SCREENS[currentScreenIndex];
  // Calculate progress only for quiz questions (screens 1 to 12)
  const isQuizQuestion = currentScreenIndex > 0 && currentScreenIndex <= 12;
  const progress = isQuizQuestion ? currentScreenIndex / 12 * 100 : 0;
  const getQuestionNumber = (index: number) => {
    let qNum = 0;
    for (let i = 0; i <= index; i++) {
      if (['single', 'multi', 'slider'].includes(QUIZ_SCREENS[i].type)) {
        qNum++;
      }
    }
    return qNum;
  };
  const renderScreen = () => {
    const isQuestionType = ['single', 'multi', 'slider'].includes(screen.type);
    const questionNumber = isQuestionType ? getQuestionNumber(currentScreenIndex) : undefined;
    const totalQuestions = QUIZ_SCREENS.filter(s => ['single', 'multi', 'slider'].includes(s.type)).length;
    switch (screen.type) {
      case 'hero':
        return <HeroScreen onStart={handleNext} data-id="element-8" />;
      case 'single':
        return <QuizLayout progress={progress} onBack={handleBack} questionNumber={questionNumber} totalQuestions={totalQuestions} data-id="element-9">
            <QuizQuestion title={screen.title!} subtitle={screen.subtitle} note={screen.note} noteTitle={screen.noteTitle} options={screen.options!} selectedIds={answers[screen.id] ? [answers[screen.id]] : []} onSelect={id => setAnswer(screen.id, id)} onContinue={handleNext} data-id="element-10" />
          </QuizLayout>;
      case 'multi':
        return <QuizLayout progress={progress} onBack={handleBack} questionNumber={questionNumber} totalQuestions={totalQuestions} data-id="element-11">
            <QuizQuestion title={screen.title!} note={screen.note} options={screen.options!} multiSelect={true} maxSelections={screen.maxSelections} selectedIds={answers[screen.id] || []} onSelect={id => toggleMultiAnswer(screen.id, id)} onContinue={handleNext} data-id="element-12" />
          </QuizLayout>;
      case 'slider':
        return <QuizLayout progress={progress} onBack={handleBack} questionNumber={questionNumber} totalQuestions={totalQuestions} data-id="element-13">
            <SliderQuestion title={screen.title!} note={screen.note} value={answers[screen.id] || 5} onChange={val => setAnswer(screen.id, val)} onContinue={handleNext} data-id="element-14" />
          </QuizLayout>;
      case 'validation':
        return <QuizLayout progress={progress} onBack={handleBack} data-id="element-15">
            <ValidationScreen onContinue={handleNext} data-id="element-16" />
          </QuizLayout>;
      case 'loading':
        return <LoadingScreen onComplete={handleNext} data-id="element-17" />;
      case 'result':
        return <ResultScreen onContinue={handleNext} data-id="element-18" />;
      case 'sales':
        return <SalesPage data-id="element-19" />;
      default:
        return <div data-id="element-20">Screen not found</div>;
    }
  };
  return <div className="min-h-screen bg-gray-100 font-sans text-brand-dark" data-id="element-21">
      {/* Desktop wrapper to simulate mobile view */}
      <div className="max-w-md mx-auto bg-white min-h-screen shadow-2xl relative overflow-hidden" data-id="element-22">
        <AnimatePresence mode="wait" data-id="element-23">
          <motion.div key={currentScreenIndex} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="min-h-screen flex flex-col" data-id="element-24">
            {renderScreen()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}